/**
 * Centralized color palette for the application.
 * All colors should be imported from this file to ensure consistency.
 */

// =============================================================================
// BRAND COLORS
// =============================================================================

export const colors = {
  // ---------------------------------------------------------------------------
  // Primary (Brand Green)
  // ---------------------------------------------------------------------------
  primary: {
    main: '#34C759',
    subtle: '#7AE582',
    tint: '#E6F7F0',
    spotlight: '#96FFB1',
  },

  // ---------------------------------------------------------------------------
  // Success (Emerald Green)
  // ---------------------------------------------------------------------------
  success: {
    main: '#10B981',
    light: '#ECFDF5',
  },

  // ---------------------------------------------------------------------------
  // Warning
  // ---------------------------------------------------------------------------
  warning: {
    main: '#FFC212',
  },

  // ---------------------------------------------------------------------------
  // Danger / Error
  // ---------------------------------------------------------------------------
  danger: {
    main: '#FF0022',
  },

  // ---------------------------------------------------------------------------
  // Info
  // ---------------------------------------------------------------------------
  info: {
    main: '#9DCCFF',
    light: '#EFF6FF',
  },

  // ---------------------------------------------------------------------------
  // Neutral / Text
  // ---------------------------------------------------------------------------
  text: {
    primary: '#2D3436',
    secondary: '#636E72',
    muted: '#94A3B8',
  },

  // ---------------------------------------------------------------------------
  // Background
  // ---------------------------------------------------------------------------
  background: {
    main: '#F8FAFC',
    secondary: '#FFFFFF',
    surface: '#FFFFFF',
  },

  // ---------------------------------------------------------------------------
  // Border
  // ---------------------------------------------------------------------------
  border: {
    main: '#EFEBFF',
    secondary: '#F0F0F0',
    accent: '#E6F7F0',
  },

  // ---------------------------------------------------------------------------
  // Purple (Accent)
  // ---------------------------------------------------------------------------
  purple: {
    main: '#6737FF',
    tint: '#F3EFFF',
    light: '#F0EBFF',
  },

  // ---------------------------------------------------------------------------
  // Unified plan credits (aligned with the mockup)
  // ---------------------------------------------------------------------------
  planCredits: {
    green: '#34C759', // gauge / nominal remaining %
    amber: '#DC8A16', // gauge / % / overage amount
    amberSoft: '#FFF5E4', // banner background
    amberBorder: '#F0D3A4', // banner border
    amberDark: '#8F5709', // banner title / text
    amberMuted: '#A9650C', // sub-line (units · €)
  },
} as const;

// =============================================================================
// DARK THEME COLORS
// =============================================================================

export const darkColors = {
  primary: {
    main: '#34C759',
    subtle: '#7AE582',
    tint: '#134E2A',
    spotlight: '#96FFB1',
  },

  success: {
    main: '#10B981',
    light: '#064E3B',
  },

  warning: {
    main: '#FBBF24',
    light: '#78350F',
  },

  danger: {
    main: '#EF4444',
    light: '#7F1D1D',
  },

  info: {
    main: '#60A5FA',
    light: '#1E3A5F',
  },

  text: {
    primary: '#F1F5F9',
    secondary: '#94A3B8',
    muted: '#64748B',
  },

  background: {
    main: '#0F172A',
    secondary: '#1E293B',
    surface: '#1E293B',
  },

  border: {
    main: '#334155',
    secondary: '#334155',
    accent: '#334155',
  },

  purple: {
    main: '#A78BFA',
    tint: '#2E1065',
    light: '#2E1065',
  },

  card: {
    background: '#1E293B',
    border: '#334155',
    hover: '#334155',
  },
} as const;

// =============================================================================
// SEMANTIC ALIASES (for specific use cases)
// =============================================================================

export const semanticColors = {
  // Icon colors
  icon: {
    primary: colors.primary.main,
    success: colors.success.main,
    muted: colors.text.muted,
  },

  // Icon background colors
  iconBackground: {
    green: colors.success.light,
    blue: colors.info.light,
  },

  // Badge colors
  badge: {
    positive: {
      background: colors.success.light,
      text: colors.success.main,
    },
    negative: {
      background: '#FEF2F2',
      text: colors.danger.main,
    },
  },

  // Menu colors
  menu: {
    item: colors.text.secondary,
    itemHover: colors.text.primary,
    itemSelected: colors.text.primary,
    selectedBackground: colors.primary.tint,
  },
} as const;

export const darkSemanticColors = {
  icon: {
    primary: darkColors.primary.main,
    success: darkColors.success.main,
    muted: darkColors.text.muted,
  },

  iconBackground: {
    green: darkColors.success.light,
    blue: darkColors.info.light,
  },

  badge: {
    positive: {
      background: darkColors.success.light,
      text: darkColors.success.main,
    },
    negative: {
      background: darkColors.danger.light,
      text: darkColors.danger.main,
    },
  },

  menu: {
    item: darkColors.text.secondary,
    itemHover: darkColors.text.primary,
    itemSelected: darkColors.text.primary,
    selectedBackground: darkColors.primary.tint,
  },
} as const;

// =============================================================================
// CSS VARIABLE MAPPING
// =============================================================================

/**
 * Mapping to CSS custom properties defined in FrontendLeedflow.css
 * Use these when you need to reference CSS variables in inline styles
 */
export const cssVars = {
  primary: 'var(--primary-color)',
  primarySubtle: 'var(--primary-color-subtle)',
  primaryTint: 'var(--primary-color-tint)',
  success: 'var(--success-color)',
  successLight: 'var(--success-color-light)',
  warning: 'var(--warning-color)',
  danger: 'var(--danger-color)',
  info: 'var(--info-color)',
  textBase: 'var(--text-base-color)',
  textSecondary: 'var(--text-secondary-color)',
  textMuted: 'var(--text-muted-color)',
  background: 'var(--background-color)',
  backgroundSecondary: 'var(--background-secondary-color)',
  surface: 'var(--surface-color)',
  border: 'var(--border-color)',
  purple: 'var(--purple-color)',
  purpleTint: 'var(--purple-tint)',
} as const;

// Type exports
export type Colors = typeof colors;
export type SemanticColors = typeof semanticColors;
